import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAdminStats,
  getAdminUsers,
  updateAdminUser,
  resetAdminUserPassword,
  deleteAdminUser,
} from '../services/adminService';
import './AdminPage.css';

const ROLES = ['admin', 'member', 'guest'];

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value">{value ?? '—'}</div>
      <div className="admin-stat-label">{label}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [copiedUserId, setCopiedUserId] = useState('');

  const handleCopyUserId = async (id) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(String(id));
      setCopiedUserId(String(id));
      window.setTimeout(() => {
        setCopiedUserId((prev) => (prev === String(id) ? '' : prev));
      }, 1500);
    } catch (err) {
      console.error('Failed to copy user ID:', err);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await getAdminStats();
      setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to load admin stats:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminUsers({ search });
      setUsers(res.data.users || []);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === 'users') {
      loadUsers();
    }
  }, [tab, loadUsers]);

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleRoleChange = async (userId, role) => {
    try {
      const res = await updateAdminUser(userId, { role });
      setUsers((prev) => prev.map((item) => (item._id === userId ? res.data.user : item)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update role');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      const res = await updateAdminUser(userId, { isActive });
      setUsers((prev) => prev.map((item) => (item._id === userId ? res.data.user : item)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteAdminUser(userId);
      setUsers((prev) => prev.filter((item) => item._id !== userId));
      setConfirmDeleteId(null);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    const nextPassword = resetPasswordValue.trim();
    if (nextPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      await resetAdminUserPassword(userId, nextPassword);
      setResetPasswordId(null);
      setResetPasswordValue('');
      alert('Password reset successfully');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to reset password');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Admin Panel</h1>
        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === 'overview' ? 'active' : ''}`}
            onClick={() => setTab('overview')}
          >
            Overview
          </button>
          <button
            className={`admin-tab ${tab === 'users' ? 'active' : ''}`}
            onClick={() => setTab('users')}
          >
            Users
          </button>
        </div>
      </div>

      {tab === 'overview' && (
        <div className="admin-section">
          {!stats ? (
            <p className="admin-muted">Loading stats...</p>
          ) : (
            <div className="admin-stats-grid">
              <StatCard label="Total Users" value={stats.totalUsers} sub={`${stats.activeUsers} active`} />
              <StatCard label="Inactive Users" value={stats.inactiveUsers} />
              <StatCard label="Admins" value={stats.adminUsers} />
              <StatCard label="Spaces" value={stats.totalSpaces} />
              <StatCard label="Threads" value={stats.totalThreads} />
              <StatCard label="Messages" value={stats.totalMessages} />
              <StatCard label="Connections" value={stats.totalConnections} />
            </div>
          )}
        </div>
      )}

      {tab === 'users' && (
        <div className="admin-section">
          <div className="admin-users-toolbar">
            <input
              className="admin-search"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn-sm btn-secondary" onClick={loadUsers}>Refresh</button>
          </div>

          {error && <p className="admin-error">{error}</p>}

          {loading ? (
            <p className="admin-muted">Loading users...</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((item) => (
                    <tr key={item._id} className={!item.isActive ? 'row-inactive' : ''}>
                      <td>
                        <div className="admin-user-cell">
                          <div className="admin-user-avatar">{(item.name || '?')[0].toUpperCase()}</div>
                          <div>
                            <div className="admin-user-name">{item.name}</div>
                            <div className="admin-user-email">{item.email}</div>
                            <div className="admin-user-id-row">
                              <span className="admin-user-id">ID: {item._id}</span>
                              <button
                                type="button"
                                className="admin-copy-id-btn"
                                onClick={() => handleCopyUserId(item._id)}
                                title="Copy user ID"
                                aria-label={`Copy user ID for ${item.name || 'user'}`}
                              >
                                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                                  <path d="M9 9h10v12H9z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                                  <path d="M5 3h10v4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                                  <path d="M5 3v12h4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                                </svg>
                                <span>{copiedUserId === String(item._id) ? 'Copied' : 'Copy'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <select
                          className={`admin-role-select role-${item.role}`}
                          value={item.role}
                          onChange={(e) => handleRoleChange(item._id, e.target.value)}
                          disabled={String(item._id) === String(user?._id)}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          className={`admin-status-btn ${item.isActive ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleActive(item._id, !item.isActive)}
                          disabled={String(item._id) === String(user?._id)}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="admin-muted">
                        {new Date(item.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td>
                        {String(item._id) !== String(user?._id) && (
                          resetPasswordId === item._id ? (
                            <div className="admin-inline-actions">
                              <input
                                type="password"
                                className="admin-password-input"
                                placeholder="New password..."
                                value={resetPasswordValue}
                                onChange={(e) => setResetPasswordValue(e.target.value)}
                                autoFocus
                              />
                              <button className="btn-sm btn-secondary" onClick={() => handleResetPassword(item._id)}>
                                Save
                              </button>
                              <button
                                className="btn-sm"
                                onClick={() => {
                                  setResetPasswordId(null);
                                  setResetPasswordValue('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : confirmDeleteId === item._id ? (
                            <span className="admin-confirm-row">
                              <span className="admin-muted">Sure?</span>
                              <button className="btn-danger-sm" onClick={() => handleDeleteUser(item._id)}>
                                Yes, delete
                              </button>
                              <button className="btn-sm" onClick={() => setConfirmDeleteId(null)}>
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <div className="admin-inline-actions">
                              <button
                                className="btn-sm btn-secondary"
                                onClick={() => {
                                  setConfirmDeleteId(null);
                                  setResetPasswordId(item._id);
                                  setResetPasswordValue('');
                                }}
                              >
                                Reset Password
                              </button>
                              <button className="btn-danger-sm" onClick={() => setConfirmDeleteId(item._id)}>
                                Delete
                              </button>
                            </div>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && !loading && (
                    <tr>
                      <td colSpan="5" className="admin-muted admin-empty-cell">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}